import { z } from 'zod';

import { getFeatureFlagsConfigAction } from '@kit/feature-flags/server';

export type { FeatureFlagsConfig } from '@kit/feature-flags';

type LanguagePriority = 'user' | 'application';

const FeatureFlagsSchema = z.object({
  enableThemeToggle: z.boolean({
    description: 'Enable theme toggle in the user interface.',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_THEME_TOGGLE',
  }),
  enableAccountDeletion: z.boolean({
    description: 'Enable personal account deletion.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION',
  }),
  enableTeamDeletion: z.boolean({
    description: 'Enable team deletion.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION',
  }),
  enableTeamAccounts: z.boolean({
    description: 'Enable team accounts.',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS',
  }),
  enableTeamCreation: z.boolean({
    description: 'Enable team creation.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION',
  }),
  enablePersonalAccountBilling: z.boolean({
    description: 'Enable personal account billing.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING',
  }),
  enableTeamAccountBilling: z.boolean({
    description: 'Enable team account billing.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING',
  }),
  languagePriority: z
    .enum(['user', 'application'], {
      required_error: 'Provide the variable NEXT_PUBLIC_LANGUAGE_PRIORITY',
      description: `If set to user, use the user's preferred language. If set to application, use the application's default language.`,
    })
    .default('application'),
  enableNotifications: z.boolean({
    description: 'Enable notifications functionality',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_NOTIFICATIONS',
  }),
  realtimeNotifications: z.boolean({
    description: 'Enable realtime for the notifications functionality',
    required_error: 'Provide the variable NEXT_PUBLIC_REALTIME_NOTIFICATIONS',
  }),
  enableVersionUpdater: z.boolean({
    description: 'Enable version updater',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_VERSION_UPDATER',
  }),
  enableSavedSignatures: z.boolean({
    description: 'Enable saved signatures functionality',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_SAVED_SIGNATURES',
  }),
  enableCoaching: z.boolean({
    description: 'Enable coaching pages and navigation',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_COACHING',
  }),
  enableAmbassadors: z.boolean({
    description: 'Enable the brand-ambassadors module (pages + navigation)',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_AMBASSADORS',
  }),
  enableMarketplaceCommerce: z.boolean({
    description:
      'Enable Stripe Connect payments + commission on the marketplace. When false, the marketplace runs in free / contact-seller mode (buyers DM the seller via /home/inbox).',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_MARKETPLACE_COMMERCE',
  }),
});

/**
 * Static feature flags config from environment variables
 * Used as fallback during build time or when database is unavailable
 * @deprecated Use getFeatureFlagsConfig() for database-backed flags
 */
const featuresFlagConfig = FeatureFlagsSchema.parse({
  enableThemeToggle: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_THEME_TOGGLE,
    true,
  ),
  enableAccountDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION,
    true,
  ),
  enableTeamDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION,
    true,
  ),
  enableTeamAccounts: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS,
    false,
  ),
  enableTeamCreation: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION,
    false,
  ),
  enablePersonalAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING,
    false,
  ),
  enableTeamAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING,
    false,
  ),
  languagePriority: process.env
    .NEXT_PUBLIC_LANGUAGE_PRIORITY as LanguagePriority,
  enableNotifications: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_NOTIFICATIONS,
    true,
  ),
  realtimeNotifications: getBoolean(
    process.env.NEXT_PUBLIC_REALTIME_NOTIFICATIONS,
    true,
  ),
  enableVersionUpdater: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_VERSION_UPDATER,
    true,
  ),
  enableSavedSignatures: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_SAVED_SIGNATURES,
    true,
  ),
  enableCoaching: getBoolean(process.env.NEXT_PUBLIC_ENABLE_COACHING, false),
  // Disabled by default (2026-07-05): the module is gated off until the
  // brand-ambassadors flow is ready. Flip NEXT_PUBLIC_ENABLE_AMBASSADORS=true
  // to re-enable.
  enableAmbassadors: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_AMBASSADORS,
    false,
  ),
  enableMarketplaceCommerce: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_MARKETPLACE_COMMERCE,
    false,
  ),
} satisfies z.infer<typeof FeatureFlagsSchema>);

export default featuresFlagConfig;

/**
 * Fetch feature flags from database (async)
 * Use this in server components and server actions
 */
export async function getFeatureFlagsConfig() {
  return getFeatureFlagsConfigAction();
}

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }

  return defaultValue;
}
